import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import HTLDSlide3Mobile from "./HTLDSlide3Mobile";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { slide3Content, slide3RowKeys, useSlide3 } from "./slide3.data";

const accent = "#4fc3f7";
const font   = FONT_BODY;

const cellPad  = "clamp(4px,0.5vw,7px) clamp(9px,1vw,14px)";
const cellFont = "clamp(12px,1.0vw,15px)";

const S: Record<string, CSSProperties> = {
root: {
background: "#040f18", fontFamily: font, color: "#d0e8f8",
width: "100%", height: "100%",
display: "flex", flexDirection: "column",
overflow: "hidden", boxSizing: "border-box",
},
// Shared header row: breadcrumb (left) + slide title (right), above the divider line
subtitleBar: {
background: "#050f1a", borderBottom: "1px solid rgba(79,195,247,0.1)",
padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
display: "flex", alignItems: "center", justifyContent: "space-between",
gap: "clamp(12px,2vw,28px)", flexShrink: 0,
},
body: {
flex: 1, minHeight: 0,
display: "flex", gap: "clamp(10px,1.5vw,20px)",
padding: "clamp(8px,1vw,14px) clamp(16px,3vw,40px) clamp(6px,0.8vw,10px)",
alignItems: "stretch", overflow: "hidden",
},
tableWrap: {
flex: 1, minWidth: 0,
display: "flex", flexDirection: "column",
gap: "clamp(4px,0.5vw,7px)",
},
journeyLabel: {
fontFamily: FONT_BODY, display: "flex", justifyContent: "space-between",
fontSize: "clamp(10px,0.85vw,12px)", fontStyle: "normal",
color: "#4878a0", flexShrink: 0,
},
table: {
width: "100%", borderCollapse: "separate", borderSpacing: 0,
borderRadius: 8, overflow: "hidden",
border: "1px solid rgba(79,195,247,0.16)",
boxShadow: "0 4px 40px rgba(0,0,0,0.5)", tableLayout: "fixed",
flexShrink: 0,
},
th:    { background: "#0c2a44", padding: cellPad, fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", fontWeight: 600, textAlign: "left", borderBottom: "1px solid rgba(79,195,247,0.22)", fontFamily: FONT_BODY, verticalAlign: "bottom" },
thSub: { display: "block", fontSize: "clamp(8px,0.65vw,10px)", fontWeight: 400, marginTop: 1, opacity: 0.65, letterSpacing: 0.8, fontFamily: FONT_MONO, fontStyle: "normal" },
tdLabel: { fontFamily: FONT_BODY, padding: cellPad, fontSize: cellFont, fontStyle: "normal", lineHeight: 1.38, borderTop: "1px solid rgba(79,195,247,0.07)", verticalAlign: "top", fontWeight: 400, color: "#a0d8f5", background: "rgba(8,24,40,0.65)", borderRight: "1px solid rgba(79,195,247,0.1)", whiteSpace: "nowrap", width: "16%" },
tdOdd:  { fontFamily: FONT_BODY, padding: cellPad, fontSize: cellFont, fontStyle: "normal", lineHeight: 1.38, borderTop: "1px solid rgba(79,195,247,0.07)", verticalAlign: "top", color: "#a8c8e8", background: "rgba(7,21,32,0.85)", borderLeft: "1px solid rgba(79,195,247,0.05)" },
tdEven: { fontFamily: FONT_BODY, padding: cellPad, fontSize: cellFont, fontStyle: "normal", lineHeight: 1.38, borderTop: "1px solid rgba(79,195,247,0.07)", verticalAlign: "top", color: "#a8c8e8", background: "rgba(4,14,24,0.95)", borderLeft: "1px solid rgba(79,195,247,0.05)" },
tdHL:   { fontFamily: FONT_BODY, padding: cellPad, fontSize: cellFont, fontStyle: "normal", lineHeight: 1.38, borderTop: "1px solid rgba(79,195,247,0.2)",  verticalAlign: "top", fontWeight: 400, color: accent, background: "rgba(8,24,40,0.65)", borderRight: "1px solid rgba(79,195,247,0.1)", whiteSpace: "nowrap", width: "16%" },
tdHLC:  { fontFamily: FONT_BODY, padding: cellPad, fontSize: cellFont, fontStyle: "normal", lineHeight: 1.38, borderTop: "1px solid rgba(79,195,247,0.2)",  verticalAlign: "top", color: "#c0daf5", background: "rgba(7,21,32,0.85)", borderLeft: "1px solid rgba(79,195,247,0.05)" },
tdWL:   { fontFamily: FONT_BODY, padding: cellPad, fontSize: cellFont, fontStyle: "normal", lineHeight: 1.38, borderTop: "1px solid rgba(79,195,247,0.18)", verticalAlign: "top", fontWeight: 400, color: accent, background: "rgba(8,24,40,0.7)",  borderRight: "1px solid rgba(79,195,247,0.1)", whiteSpace: "nowrap", width: "16%" },
tdWC:   { fontFamily: FONT_BODY, padding: cellPad, fontSize: cellFont, fontStyle: "normal", lineHeight: 1.38, borderTop: "1px solid rgba(79,195,247,0.18)", verticalAlign: "top", color: "#a8c8e8", background: "rgba(8,24,40,0.4)",  borderLeft: "1px solid rgba(79,195,247,0.05)" },
footnote: {
fontFamily: FONT_BODY, fontSize: "clamp(10px,0.9vw,13px)", fontStyle: "normal", color: "#3a6888",
borderTop: "1px solid rgba(79,195,247,0.08)",
paddingTop: "clamp(5px,0.6vw,8px)",
lineHeight: 1.5, flexShrink: 0,
},
// Panel is a stretched flex column so the iceberg fills the full height
panel: { width: "clamp(200px,26vw,380px)", flexShrink: 0, display: "flex", flexDirection: "column" },
};

const badge = (bg: string, color: string): CSSProperties => ({
display: "inline-block", padding: "1px 7px", borderRadius: 10,
fontSize: "clamp(9px,0.72vw,11px)", fontStyle: "normal", fontFamily: FONT_MONO,
marginTop: 2, background: bg, color,
});

// ─── Iceberg Canvas ──────────────────────────────────────────────────────────
const IcebergCanvas: React.FC = () => {
const canvasRef = useRef<HTMLCanvasElement>(null);
const drawFnRef = useRef<null | (() => void)>(null);
const dtRef     = useRef<number>(0);

useEffect(() => {
  const canvas = canvasRef.current; if (!canvas) return;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
  const W = 480, H = 560;
  canvas.width = W; canvas.height = H;

  const WY = 200; // waterline
  const labels = pick(slide3Content).canvas.labels; // slides remount on language change

  const above1: [number,number][] = [[145,6],[55,200],[235,200]];
  const above2: [number,number][] = [[210,69],[135,200],[295,200]];
  const below:  [number,number][] = [
    [55,200],[43,230],[53,265],[40,300],[50,340],[63,375],
    [83,410],[100,440],[125,475],[145,500],[160,520],[190,535],
    [215,515],[240,485],[265,450],[285,410],[303,370],[310,330],
    [295,290],[305,255],[295,200],
  ];

  let frame = 0;

  const stars = Array.from({ length: 130 }, () => ({
    x: Math.random() * W, y: Math.random() * (WY - 8),
    r: 0.3 + Math.random() * 1.5,
    tw: Math.random() * Math.PI * 2, spd: 0.010 + Math.random() * 0.024,
  }));

  const bubbles = Array.from({ length: 18 }, () => ({
    x: 55 + Math.random() * 240,
    y: WY + 25 + Math.random() * (H - WY - 55),
    r: 0.9 + Math.random() * 2.8,
    speed: 0.22 + Math.random() * 0.55,
    wobble: Math.random() * Math.PI * 2,
  }));

  const plankton = Array.from({ length: 40 }, () => ({
    x: 35 + Math.random() * 310, y: WY + 15 + Math.random() * (H - WY - 30),
    r: 0.4 + Math.random() * 1.2,
    vy: 0.05 + Math.random() * 0.12, vx: (Math.random() - 0.5) * 0.15,
    tw: Math.random() * Math.PI * 2, tws: 0.008 + Math.random() * 0.016,
  }));

  function waveY(x: number, t: number) {
    const wp = t * 0.022;
    return WY + 7 * Math.sin(x * 0.032 + wp)
             + 3.5 * Math.sin(x * 0.065 - wp * 1.4)
             + 1.5 * Math.sin(x * 0.11 + wp * 0.8);
  }

  function poly(pts: [number,number][]) {
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    pts.slice(1).forEach(([x, y]) => ctx.lineTo(x, y));
    ctx.closePath();
  }

  function draw() {
    ctx.clearRect(0, 0, W, H);
    const p  = 0.5 + 0.5 * Math.sin(frame * 0.018);
    const sp = 0.5 + 0.5 * Math.sin(frame * 0.008);
    const MX = 390, MY = 55;

    // ── Night sky ──────────────────────────────────────────────────────
    const skyGrd = ctx.createLinearGradient(0, 0, 0, WY);
    skyGrd.addColorStop(0, "#010810"); skyGrd.addColorStop(0.38, "#061525"); skyGrd.addColorStop(1, "#0d2845");
    ctx.fillStyle = skyGrd; ctx.fillRect(0, 0, W, WY);

    ([
      [80,  62, 120, "rgba(18,35,95,0.28)"],
      [348, 78,  95, "rgba(28,15,85,0.22)"],
    ] as [number,number,number,string][]).forEach(([nx,ny,nr,nc]) => {
      const g = ctx.createRadialGradient(nx,ny,0,nx,ny,nr);
      g.addColorStop(0, nc); g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath(); ctx.arc(nx,ny,nr,0,Math.PI*2); ctx.fillStyle=g; ctx.fill();
    });

    stars.forEach(s => {
      const tw = 0.25 + 0.75 * Math.abs(Math.sin(frame * s.spd + s.tw));
      ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI*2);
      ctx.fillStyle = `rgba(255,255,255,${(tw*0.88).toFixed(2)})`; ctx.fill();
    });

    // ── Moon ──────────────────────────────────────────────────────────
    ([
      [100, 0.04], [74, 0.08], [55, 0.13], [40, 0.22],
    ] as [number,number][]).forEach(([r, a]) => {
      const g = ctx.createRadialGradient(MX,MY,0,MX,MY,r);
      g.addColorStop(0, `rgba(255,250,200,${(a+0.02*p).toFixed(3)})`); g.addColorStop(1,"rgba(255,250,200,0)");
      ctx.beginPath(); ctx.arc(MX,MY,r,0,Math.PI*2); ctx.fillStyle=g; ctx.fill();
    });
    const mGrd = ctx.createRadialGradient(MX-4,MY-5,0,MX,MY,18);
    mGrd.addColorStop(0,"#fffef8"); mGrd.addColorStop(0.6,"#fffde0"); mGrd.addColorStop(1,"#fff5b0");
    ctx.beginPath(); ctx.arc(MX,MY,18,0,Math.PI*2); ctx.fillStyle=mGrd; ctx.fill();
    ctx.beginPath(); ctx.arc(MX-6,MY-6,6,0,Math.PI*2); ctx.fillStyle="rgba(200,190,130,0.10)"; ctx.fill();
    ctx.beginPath(); ctx.arc(MX+5,MY+6,4,0,Math.PI*2); ctx.fillStyle="rgba(190,180,120,0.08)"; ctx.fill();

    // ── Water body ────────────────────────────────────────────────────
    const wg = ctx.createLinearGradient(0, WY, 0, H);
    wg.addColorStop(0,"#0C1A32"); wg.addColorStop(0.38,"#061020");
    wg.addColorStop(0.58,"#040D1A"); wg.addColorStop(1,"#010308");
    ctx.fillStyle = wg; ctx.fillRect(0, WY, W, H - WY);

    // ── Caustic beams (clipped inside submerged ice) ──────────────────
    ctx.save();
    poly(below); ctx.clip();
    [88, 128, 168, 210, 258].forEach((bx, i) => {
      const sh = 5.5 * Math.sin(frame * 0.016 + i * 1.3);
      const peakA = 0.18 + 0.08 * Math.sin(frame * 0.022 + i * 0.9);
      const bg = ctx.createLinearGradient(bx+sh, WY, bx+sh*2, H);
      bg.addColorStop(0, `rgba(160,225,255,${peakA.toFixed(3)})`);
      bg.addColorStop(0.32, `rgba(80,170,235,${(peakA*0.42).toFixed(3)})`);
      bg.addColorStop(1, "rgba(35,95,200,0)");
      ctx.beginPath();
      ctx.moveTo(bx+sh-9, WY); ctx.lineTo(bx+sh+9, WY);
      ctx.lineTo(bx+sh*2.2+20, H); ctx.lineTo(bx+sh*2.2-12, H);
      ctx.closePath(); ctx.fillStyle = bg; ctx.fill();
    });
    ctx.restore();

    // ── Submerged ice ─────────────────────────────────────────────────
    poly(below);
    const ibGrd = ctx.createLinearGradient(0, WY, 0, 535);
    ibGrd.addColorStop(0,"#2878C0"); ibGrd.addColorStop(0.42,"#1660A8");
    ibGrd.addColorStop(0.51,"#1050A0"); ibGrd.addColorStop(0.55,"#0A2858"); ibGrd.addColorStop(1,"#020510");
    ctx.fillStyle = ibGrd; ctx.fill();

    ctx.save(); poly(below); ctx.clip();
    ([
      [[[55,200],[175,255],[295,200]],   0.22],
      [[[175,255],[43,302],[175,315]],   0.16],
      [[[175,255],[310,302],[175,315]],  0.17],
      [[[43,302],[40,355],[175,342]],    0.13],
      [[[310,302],[310,355],[175,342]],  0.14],
      [[[50,388],[63,428],[175,415]],    0.23],
      [[[303,388],[295,428],[175,415]],  0.19],
      [[[63,428],[100,522],[175,490]],   0.16],
      [[[295,428],[265,522],[175,490]],  0.13],
    ] as [[number,number][], number][]).forEach(([pts, a]) => {
      poly(pts); ctx.fillStyle = `rgba(80,165,240,${a})`; ctx.fill();
    });
    ctx.restore();

    poly(below);
    ctx.strokeStyle = `rgba(70,195,255,${(0.08+0.04*p).toFixed(3)})`; ctx.lineWidth = 9; ctx.stroke();
    ctx.strokeStyle = `rgba(140,230,255,${(0.20+0.08*p).toFixed(3)})`; ctx.lineWidth = 2.5; ctx.stroke();

    // ── Plankton ──────────────────────────────────────────────────────
    plankton.forEach(pl => {
      pl.y += pl.vy; pl.x += pl.vx; pl.tw += pl.tws;
      if (pl.y > H + 5) { pl.y = WY + 5; pl.x = 35 + Math.random() * 310; }
      if (pl.x < 28 || pl.x > 322) pl.vx *= -1;
      const tw = 0.25 + 0.75 * Math.abs(Math.sin(pl.tw));
      ctx.beginPath(); ctx.arc(pl.x, pl.y, pl.r, 0, Math.PI*2);
      ctx.fillStyle = `rgba(100,205,255,${(tw*0.28).toFixed(2)})`; ctx.fill();
    });

    // ── Bubbles ───────────────────────────────────────────────────────
    bubbles.forEach(b => {
      b.y -= b.speed; b.wobble += 0.042; b.x += 0.45 * Math.sin(b.wobble);
      if (b.y < WY) { b.y = H - 15; b.x = 55 + Math.random() * 240; }
      const a = 0.18 + 0.10 * Math.abs(Math.sin(b.wobble));
      const bg = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r * 2.2);
      bg.addColorStop(0, `rgba(180,245,255,${(a*0.35).toFixed(3)})`); bg.addColorStop(1, "rgba(180,245,255,0)");
      ctx.beginPath(); ctx.arc(b.x, b.y, b.r * 2.2, 0, Math.PI*2); ctx.fillStyle = bg; ctx.fill();
      ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, Math.PI*2);
      ctx.strokeStyle = `rgba(180,242,255,${a.toFixed(3)})`; ctx.lineWidth = 0.85; ctx.stroke();
      ctx.beginPath(); ctx.arc(b.x - b.r*0.35, b.y - b.r*0.35, b.r*0.28, 0, Math.PI*2);
      ctx.fillStyle = `rgba(230,255,255,${(a*0.65).toFixed(3)})`; ctx.fill();
    });

    // ── Ice reflection ────────────────────────────────────────────────
    ctx.save();
    ctx.beginPath(); ctx.rect(0, WY, W, H - WY); ctx.clip();
    [above1, above2].forEach(pts => {
      const rPts: [number,number][] = pts.map(([px, py]) => [
        px + Math.sin(frame * 0.02 + px * 0.025) * 4,
        2 * WY - py,
      ]);
      poly(rPts);
      const rg = ctx.createLinearGradient(0, WY, 0, WY * 2);
      rg.addColorStop(0, "rgba(200,238,255,0.11)"); rg.addColorStop(1, "rgba(200,238,255,0.01)");
      ctx.fillStyle = rg; ctx.fill();
    });
    ctx.restore();

    // ── Above-water ice ───────────────────────────────────────────────
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(above1[0][0], above1[0][1]);
    above1.slice(1).forEach(([x,y]) => ctx.lineTo(x,y)); ctx.closePath();
    ctx.moveTo(above2[0][0], above2[0][1]);
    above2.slice(1).forEach(([x,y]) => ctx.lineTo(x,y)); ctx.closePath();
    ctx.clip();
    const igGrd = ctx.createRadialGradient(175, 6, 0, 175, 6, 215);
    igGrd.addColorStop(0,    "rgba(255,255,255,0.92)");
    igGrd.addColorStop(0.18, "rgba(240,255,255,0.66)");
    igGrd.addColorStop(0.40, "rgba(200,242,255,0.36)");
    igGrd.addColorStop(0.65, "rgba(155,222,255,0.12)");
    igGrd.addColorStop(1,    "rgba(80,180,255,0)");
    ctx.fillStyle = igGrd; ctx.fillRect(0, 0, W, WY);
    ctx.restore();

    [above1, above2].forEach(pts => {
      poly(pts);
      const ag = ctx.createLinearGradient(0, 6, 0, 200);
      ag.addColorStop(0,"#FAFEFF"); ag.addColorStop(0.20,"#E0F5FF"); ag.addColorStop(0.60,"#A8D8FF"); ag.addColorStop(1,"#72B8E8");
      ctx.fillStyle = ag; ctx.fill();
    });

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(above1[0][0], above1[0][1]);
    above1.slice(1).forEach(([x,y]) => ctx.lineTo(x,y)); ctx.closePath();
    ctx.moveTo(above2[0][0], above2[0][1]);
    above2.slice(1).forEach(([x,y]) => ctx.lineTo(x,y)); ctx.closePath();
    ctx.clip();
    ([[[55,200],[67,186],[148,166]],[[67,186],[54,172],[122,153]],
      [[145,6],[130,32],[158,54]],  [[130,32],[116,50],[142,64]]] as [number,number][][]
    ).forEach(pts => { poly(pts); ctx.fillStyle="rgba(0,38,118,0.19)"; ctx.fill(); });
    ([[[210,69],[228,98],[208,84]],   [[268,130],[284,158],[262,143]],
      [[145,6],[168,34],[152,20]],    [[168,34],[188,18],[180,42]]] as [number,number][][]
    ).forEach(pts => { poly(pts); ctx.fillStyle="rgba(255,255,255,0.32)"; ctx.fill(); });
    ctx.restore();

    [above1, above2].forEach(pts => {
      poly(pts);
      ctx.strokeStyle = `rgba(220,250,255,${(0.55+0.20*p).toFixed(3)})`; ctx.lineWidth = 1.5; ctx.stroke();
    });

    // ── Wave surface ──────────────────────────────────────────────────
    ctx.beginPath(); ctx.moveTo(0, WY);
    for (let x = 0; x <= W; x += 3) ctx.lineTo(x, waveY(x, frame));
    ctx.lineTo(W, WY+30); ctx.lineTo(0, WY+30); ctx.closePath();
    ctx.fillStyle = "rgba(50,145,215,0.48)"; ctx.fill();

    ([
      [0,   1.00, "190,238,255", 0.90, 2.2],
      [1.2, 0.82, "150,212,252", 0.58, 1.5],
      [2.5, 0.64, "110,182,242", 0.34, 0.9],
      [3.8, 0.45, "88,158,232",  0.20, 0.6],
    ] as [number,number,string,number,number][]).forEach(([off, amp, col, a, lw]) => {
      const ph = frame * 0.022 + off, base = WY + off * 0.55;
      ctx.beginPath(); ctx.moveTo(0, base);
      for (let x = 0; x <= W; x += 3)
        ctx.lineTo(x, base + 7*amp*Math.sin(x*0.032+ph) + 3.5*amp*Math.sin(x*0.065-ph*1.3));
      ctx.strokeStyle = `rgba(${col},${a})`; ctx.lineWidth = lw; ctx.stroke();
    });

    for (let x = 8; x < W - 8; x += 14 + Math.sin(x*0.08 + frame*0.04)*5) {
      const wy = waveY(x, frame);
      ctx.beginPath(); ctx.arc(x, wy, 1.0 + 0.8*Math.sin(x*0.5+frame*0.12), 0, Math.PI*2);
      ctx.fillStyle = `rgba(222,248,255,${(0.20+0.13*Math.sin(x*0.14+frame*0.07)).toFixed(3)})`; ctx.fill();
    }

    for (let i = 0; i < 9; i++) {
      const rx = MX + (i-4)*5 + Math.sin(frame*0.04+i)*5;
      const ra = (0.22 - i*0.02) * sp;
      if (ra <= 0) continue;
      ctx.beginPath(); ctx.ellipse(rx, waveY(rx, frame) + i*7.5, 7.5-i*0.25, 2, 0, 0, Math.PI*2);
      ctx.fillStyle = `rgba(255,250,180,${ra.toFixed(3)})`; ctx.fill();
    }

    const mist = ctx.createLinearGradient(0, WY-12, 0, WY+28);
    mist.addColorStop(0, "rgba(180,225,255,0)");
    mist.addColorStop(0.45, `rgba(200,238,255,${(0.06+0.03*p).toFixed(3)})`);
    mist.addColorStop(1, "rgba(180,225,255,0)");
    ctx.fillStyle = mist; ctx.fillRect(0, WY-12, W, 40);

    // ── Labels ────────────────────────────────────────────────────────
    ctx.textAlign = "left"; ctx.font = "bold 20px 'Space Grotesk',Arial,sans-serif";
    ([
      [labels[0], 313, 110],
      [labels[1], 313, 298],
      [labels[2], 313, 462],
    ] as [string,number,number][]).forEach(([text, x, y]) => {
      ctx.shadowColor = "rgba(100,200,255,0.50)"; ctx.shadowBlur = 8;
      ctx.fillStyle = "rgba(255,255,255,0.90)"; ctx.fillText(text, x, y);
    });
    ctx.shadowBlur = 0;

    frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
  }

  drawFnRef.current = draw;
  return () => { drawFnRef.current = null; };
}, []);

useVisibleAnimation(canvasRef, (dt) => { dtRef.current = dt; drawFnRef.current?.(); }, { fps: 30 });

return (
  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <canvas
      ref={canvasRef}
      style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }}
    />
  </div>
);
};

// ─── Slide content (authored at the ScaleToFit design size) ────────────────────
const HTLDSlide3Inner: React.FC = () => {
const { layers, meta } = useSlide3();
const [L0, L1, L2] = layers;
const rows = slide3RowKeys.map((k) => ({ label: meta.labels[k], c: L0[k], p: L1[k], u: L2[k] }));
// Fonts load globally (public/index.html, src/theme/fonts.ts).
const rootRef = useRef<HTMLDivElement>(null);

return (
<div style={S.root} ref={rootRef}>      {/* add ref={rootRef} */}
<SideStarField rootRef={rootRef} />   {/* add this line */}

  {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
  <div style={S.subtitleBar}>
    <div style={{
      fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#5888b0",
      fontFamily: FONT_BODY,
    }}>
      {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
      <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
    </div>
    <div style={{
      fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
      color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
      whiteSpace: "nowrap",
    }}>
      {meta.headerPlain} <span style={{ color: accent }}>{meta.headerAccent}</span>
    </div>
  </div>

  {/* Body */}
  <div style={S.body}>

    {/* Table wrap */}
    <div style={S.tableWrap}>

      {/* Title + direction labels */}
      <div style={{ flexShrink: 0 }}>
        <h1 style={{
          fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600,
          fontSize: "clamp(18px,2.1vw,28px)",
          color: "#e8f4ff", margin: "0 0 clamp(2px,0.35vw,5px)", lineHeight: 1.15,
        }}>
          {meta.titlePlain} <span style={{ color: accent }}>{meta.titleAccent}</span>
        </h1>
        <div style={S.journeyLabel}>
          <span>← {meta.journeySurface} &nbsp;·&nbsp; <span style={{ color: accent }}>{meta.journeyWaking}</span></span>
          <span><span style={{ color: accent }}>{meta.journeyDeep}</span> &nbsp;·&nbsp; {meta.journeyBelow} →</span>
        </div>
      </div>

      {/* Table */}
      <table style={S.table}>
        <thead>
          <tr>
            <th style={{ ...S.th, width: "16%" }}></th>
            <th style={{ ...S.th, color: "#85b7eb", borderLeft: "1px solid rgba(79,195,247,0.1)" }}>
              {L0.name}<span style={S.thSub}>{L0.tag}</span>
            </th>
            <th style={{ ...S.th, color: accent, borderLeft: "1px solid rgba(79,195,247,0.1)" }}>
              {L1.name}<span style={S.thSub}>{L1.tag}</span>
            </th>
            <th style={{ ...S.th, color: "#b5d4f4", borderLeft: "1px solid rgba(79,195,247,0.1)" }}>
              {L2.name}<span style={S.thSub}>{L2.tag}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ label, c, p, u }, i) => (
            <tr key={label}>
              <td style={S.tdLabel}>{label}</td>
              <td style={i % 2 === 0 ? S.tdOdd : S.tdEven}>{c}</td>
              <td style={i % 2 === 0 ? S.tdOdd : S.tdEven}>{p}</td>
              <td style={i % 2 === 0 ? S.tdOdd : S.tdEven}>{u}</td>
            </tr>
          ))}
          <tr>
            <td style={S.tdHL}>{meta.labels.sleep}</td>
            <td style={S.tdHLC}>{L0.sleep}</td>
            <td style={S.tdHLC}>{L1.sleep}</td>
            <td style={S.tdHLC}>{L2.sleep}</td>
          </tr>
          <tr>
            <td style={S.tdWL}>{meta.labels.waves}</td>
            <td style={S.tdWC}>
              <span style={badge("rgba(55,138,221,0.15)", "#85b7eb")}>{L0.waves[0].symbol} {L0.waves[0].name}</span> {L0.waves[0].desc}<br/>
              <span style={badge("rgba(55,138,221,0.1)",  "#6898c0")}>{L0.waves[1].symbol} {L0.waves[1].name}</span> {L0.waves[1].desc}
            </td>
            <td style={S.tdWC}>
              <span style={badge("rgba(24,95,165,0.2)",  "#85b7eb")}>{L1.waves[0].symbol} {L1.waves[0].name}</span> {L1.waves[0].desc}<br/>
              <span style={badge("rgba(24,95,165,0.15)", "#6898c0")}>{L1.waves[1].symbol} {L1.waves[1].name}</span> {L1.waves[1].desc}<br/>
              <span style={{ fontFamily: FONT_BODY, fontSize: "clamp(9px,0.8vw,12px)", color: accent, fontStyle: "normal" }}>{L1.wavesNote}</span>
            </td>
            <td style={S.tdWC}>
              <span style={badge("rgba(12,68,124,0.3)", "#b5d4f4")}>{L2.waves[0].symbol} {L2.waves[0].name}</span> {L2.waves[0].desc}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Footnote — always visible, flexShrink: 0 */}
      <div style={S.footnote}>
        <span style={{ color: accent }}>{meta.footnoteLead}</span> {meta.footnoteBody}
      </div>

    </div>

    {/* Panel — iceberg fills the full panel height */}
    <div style={S.panel}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <IcebergCanvas />
      </div>
    </div>

  </div>

</div>
);
};

// Scale the whole slide as one unit so it looks identical across screen
// resolutions, window sizes, and browser zoom — no reflow, no clipping.
const HTLDSlide3: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide3Mobile />
    : <ScaleToFit><HTLDSlide3Inner /></ScaleToFit>;
};

export default HTLDSlide3;